import { useEffect } from 'react'
import computeColumnWidths from './columnWidths'

const CONTROL_HEADER_CLASSES = ['selection-cell-header', 'expand-cell-header', 'Uic-Table-menuCell']

const isControlHeader = (cell) => CONTROL_HEADER_CLASSES.some((className) => cell.classList.contains(className))

// Rendered width of `text` in `element`'s font (for placeholders and ellipsized labels).
const measureText = (element, text, pseudoElement = null) => {
  if (!text) {
    return 0
  }
  const probe = document.createElement('span')
  probe.style.cssText = 'position:absolute;visibility:hidden;white-space:pre'
  probe.style.font = getComputedStyle(element, pseudoElement).font
  probe.textContent = text
  document.body.appendChild(probe)
  const { width } = probe.getBoundingClientRect()
  probe.remove()
  // Round up with a pixel to spare: sub-pixel shortfalls still trigger ellipsis.
  return Math.ceil(width) + 1
}

const horizontalPadding = (element) => {
  const style = getComputedStyle(element)
  return (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0)
}

const longestWord = (text) =>
  text.split(/\s+/).reduce((longest, word) => (word.length > longest.length ? word : longest), '')

// Minimum width of a flex row: its padding, gaps, and each part at its own minimum.
const flexRowMin = (row, partMin) => {
  const parts = [...row.children]
  const gap = parseFloat(getComputedStyle(row).columnGap) || 0
  return (
    horizontalPadding(row) + parts.reduce((sum, part) => sum + partMin(part), 0) + gap * Math.max(0, parts.length - 1)
  )
}

// How much of an ellipsized element's content is hidden.
const clippedWidth = (element) => (element ? Math.max(0, element.scrollWidth - element.clientWidth) : 0)

// react-select's search box stretches to fill its row and grows as you type.
const SEARCH_INPUT_MIN = 2

// Minimum width of one part of a multiselect's value row. The search box counts at its minimum,
// not its current width: while it has text the placeholder beside it is hidden but keeps its space.
const valuePartMin = (part) => {
  if (part.classList.contains('Uic-MultiSelectSearch-label')) {
    return measureText(part, part.textContent)
  }
  if (part.querySelector('input')) {
    return SEARCH_INPUT_MIN
  }
  return part.offsetWidth + clippedWidth(part.querySelector('.Uic-MultiSelectSearch-value'))
}

// Width the label needs to show its filter placeholder or selected value in full, or its title
// on one line. With `wrapped`, a plain title only needs room for its longest word. Typed filter
// text is not measured, so columns don't change width while someone types; it scrolls in the box.
//
// A text input can be wider than its label (`min-width`, or `width: calc(100% + 0.6rem)`). Only
// the part of the input inside the label counts as text space — anything past the label must not
// cancel out the placeholder and collapse the column.
export const measureLabelMin = (label, wrapped) => {
  const valueContainer = label.querySelector('.Uic-MultiSelectSearch-valueContainer')
  if (valueContainer) {
    return label.offsetWidth - valueContainer.offsetWidth + flexRowMin(valueContainer, valuePartMin)
  }
  const input = label.querySelector('input')
  if (input) {
    const textSpace = Math.min(input.clientWidth, label.offsetWidth) - horizontalPadding(input)
    return Math.max(0, label.offsetWidth - textSpace) + measureText(input, input.placeholder, '::placeholder')
  }
  if (label.children.length) {
    return label.scrollWidth
  }
  const text = label.textContent.trim()
  return measureText(label, wrapped ? longestWord(text) : text)
}

// A control column's header (checkbox, expand) sizes it. The row-menu header is empty, so reserve
// room for the ⋮ toggle in its body cells.
const EMPTY_CONTROL_HEADER_MIN = 32

// Uses the cell's real padding: some page styles still override the table's own.
const measureHeaderMin = (cell, wrapped) => {
  const content = cell.querySelector('.Uic-Table-headerCell')
  const contentMin = content
    ? flexRowMin(content, (part) =>
        part.classList.contains('Uic-Table-headerLabel') ? measureLabelMin(part, wrapped) : part.offsetWidth,
      )
    : Math.max(0, ...[...cell.children].map((child) => child.offsetWidth)) || EMPTY_CONTROL_HEADER_MIN
  return Math.max(0, Math.ceil(contentMin + horizontalPadding(cell)))
}

// A text filter's header is an empty input, so the header alone can measure as almost nothing.
// Keep enough width for the placeholder and for the values in the rows. The floor only needs
// each value's longest word, so values wrap between words but never break mid-word when space is
// tight; the comfort width fits each value on one line when the table has room. Both are capped,
// so one long value can't force the whole table to scroll.
const TEXT_FILTER_COLUMN_MIN = 140
const TEXT_FILTER_CONTENT_CAP = 320
// Once the table scrolls anyway, a text filter column keeps up to this width so its values read
// on a line or two instead of wrapping at every word (e.g. long app names at 1024px). A column's
// `maxWidth` lowers it, leaving that room to other columns.
const TEXT_FILTER_SCROLL_WIDTH = 240

const isTextFilterHeader = (cell) =>
  !cell.classList.contains('Uic-Table-tightColumn') && Boolean(cell.querySelector('input.Uic-Table-textFilter'))

// Measures many strings in `element`'s font. A canvas measures without a reflow per value, so
// every rendered row can be checked; jsdom has no OffscreenCanvas and falls back to the probe.
const textMeasurer = (element) => {
  const context = typeof OffscreenCanvas === 'undefined' ? null : new OffscreenCanvas(0, 0).getContext('2d')
  if (!context) {
    return (text) => measureText(element, text)
  }
  context.font = getComputedStyle(element).font
  return (text) => (text ? Math.ceil(context.measureText(text).width) + 1 : 0)
}

// The lines a body cell renders: a cell can stack blocks (a selector above a message), and each
// line needs room on its own. jsdom has no `innerText`, so there the cell is one line.
const cellLines = (bodyCell) =>
  (bodyCell.innerText ?? bodyCell.textContent)
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean)

const columnBodyCells = (cell) =>
  [...(cell.closest('table')?.tBodies?.[0]?.rows ?? [])]
    .map((row) => row.cells[cell.cellIndex])
    .filter((bodyCell) => bodyCell && bodyCell.colSpan <= 1)

const textFilterContentWidths = (cell) => {
  const bodyCells = columnBodyCells(cell)
  if (!bodyCells.length) {
    return { floor: TEXT_FILTER_COLUMN_MIN, comfort: TEXT_FILTER_COLUMN_MIN }
  }

  const padding = horizontalPadding(bodyCells[0])
  const textCap = Math.max(0, TEXT_FILTER_CONTENT_CAP - padding)
  const measure = textMeasurer(bodyCells[0])
  let widestWord = 0
  let widestLine = 0
  bodyCells.some((bodyCell) => {
    cellLines(bodyCell).forEach((line) => {
      widestLine = Math.max(widestLine, Math.min(textCap, measure(line)))
      // Measure every word: in a proportional font a shorter word can be the wider one.
      line.split(' ').forEach((word) => {
        widestWord = Math.max(widestWord, Math.min(textCap, measure(word)))
      })
    })
    // Already at the cap: the remaining rows can't widen the column.
    return widestWord >= textCap
  })

  const toColumn = (textWidth) => Math.max(TEXT_FILTER_COLUMN_MIN, textWidth > 0 ? textWidth + padding : 0)
  return { floor: toColumn(widestWord), comfort: toColumn(widestLine) }
}

// A no-wrap column's cells keep each value on one line (e.g. reference IDs that must not break), so
// the column needs its widest rendered value. It follows the rows on screen: short values give a
// narrow column, and paging or filtering to longer ones widens it.
const NO_WRAP_COLUMN_CLASS = 'Uic-Table-noWrapColumn'

const isNoWrapHeader = (cell) => cell.classList.contains(NO_WRAP_COLUMN_CLASS)

const noWrapContentWidths = (cell) => {
  const bodyCells = columnBodyCells(cell)
  if (!bodyCells.length) {
    return { floor: 0, comfort: 0 }
  }
  const measure = textMeasurer(bodyCells[0])
  const widestLine = bodyCells.reduce(
    (widest, bodyCell) => Math.max(widest, ...cellLines(bodyCell).map((line) => measure(line))),
    0,
  )
  const width = widestLine > 0 ? widestLine + horizontalPadding(bodyCells[0]) : 0
  return { floor: width, comfort: width }
}

// Only columns that opt in read their body rows; the others size from their header alone.
const columnContentWidths = (cell) => {
  if (isTextFilterHeader(cell)) {
    return textFilterContentWidths(cell)
  }
  if (isNoWrapHeader(cell)) {
    return noWrapContentWidths(cell)
  }
  return { floor: 0, comfort: 0 }
}

// The spec (the column's own width, or none) is cached on the cell because layout overwrites
// `style.width`. On reset it is re-read only when React has written a different width since.
//
// Each column gets a `floor` (title wrapped to its longest word, raised to the column's `minWidth`,
// for a text filter also room for its values' longest words, and for a no-wrap column its widest
// value) and a `comfort` width (title on one line, or the column's pixel width, and for a text
// filter its values on one line). Tight
// columns stay at the floor. A text filter also gets a `scrollFloor` for when the table scrolls anyway,
// capped by the column's `maxWidth`.
const readColumnSpec = (cell, { resetSpecs = false } = {}) => {
  const { dataset } = cell
  if (resetSpecs && cell.style.width !== dataset.uicAppliedWidth) {
    delete dataset.uicWidthSpec
  }
  if (!dataset.uicWidthSpec) {
    dataset.uicWidthSpec = isControlHeader(cell) ? 'control' : cell.style.width || 'flex'
  }

  const spec = dataset.uicWidthSpec
  const tight = spec === 'control' || cell.classList.contains('Uic-Table-tightColumn')
  const minWidth = parseFloat(cell.style.minWidth) || 0
  const maxWidth = parseFloat(cell.style.maxWidth) || Infinity
  const headerFloor = Math.max(0, measureHeaderMin(cell, true), minWidth)
  const headerComfort = tight ? headerFloor : Math.max(measureHeaderMin(cell, false), headerFloor)
  const content = columnContentWidths(cell)
  const wrappedMin = Math.max(headerFloor, content.floor)
  const oneLineMin = Math.max(headerComfort, content.comfort, wrappedMin)

  if (spec === 'control' || spec.endsWith('px')) {
    const comfort = Math.max(parseFloat(spec) || 0, oneLineMin)
    return { weight: 0, fixed: true, floor: minWidth ? wrappedMin : comfort, comfort, tight: true }
  }
  return {
    weight: spec.endsWith('%') ? parseFloat(spec) / 100 : null,
    fixed: false,
    floor: wrappedMin,
    comfort: oneLineMin,
    tight,
    scrollFloor: Math.max(wrappedMin, Math.min(content.comfort, TEXT_FILTER_SCROLL_WIDTH, maxWidth)),
  }
}

// `clientWidth` rounds to a whole pixel, so at fractional display scaling (e.g. 125%) a 665.9px
// wrapper reports 666. Columns summing to that overflow by a sub-pixel and Chrome draws a
// horizontal scrollbar, so floor the exact width (less borders and any vertical scrollbar).
export const measureAvailableWidth = (wrapper) =>
  Math.floor(wrapper.getBoundingClientRect().width) - (wrapper.offsetWidth - wrapper.clientWidth)

export const layoutTableToAvailableWidth = (wrapper, { resetSpecs = false } = {}) => {
  const table = wrapper.querySelector('table')
  const headerRow = table?.tHead?.rows?.[0]
  const available = measureAvailableWidth(wrapper)
  if (!headerRow || available <= 0) {
    return
  }

  const cells = [...headerRow.cells]
  const specs = cells.map((cell) => readColumnSpec(cell, { resetSpecs }))
  const { widths, minSum } = computeColumnWidths(available, specs)
  // Compare against the cells themselves: react-bootstrap-table re-creates some header cells
  // (checkbox, expand) without the widths set here. Widths are floored to two decimals: that
  // serializes exactly (so `style.width` reads back what was written) and never sums past the
  // available width.
  table.style.minWidth = `${Math.ceil(minSum)}px`
  cells.forEach((cell, index) => {
    const { style, dataset } = cell
    const width = `${Math.floor(widths[index] * 100) / 100}px`
    if (style.width !== width) {
      style.width = width
    }
    dataset.uicAppliedWidth = style.width
  })
}

// Print lets the table fit the page; `afterprint` lays it out again from the cached specs.
// Percentage columns keep their share: without it the browser sizes columns by content, so long
// text (e.g. a rationale) takes most of the page and squeezes the other columns.
export const releaseForPrint = (wrapper) => {
  const table = wrapper.querySelector('table')
  const headerRow = table?.tHead?.rows?.[0]
  if (!table || !headerRow) {
    return
  }
  table.style.minWidth = '0'
  table.style.width = '100%'
  ;[...headerRow.cells].forEach((cell) => {
    const { style, dataset } = cell
    style.width = dataset.uicWidthSpec?.endsWith('%') ? dataset.uicWidthSpec : ''
  })
}

// Content that changes a column's minimum without resizing anything: a selected filter value in
// the header, or new rows in the body (paging, filtering) that a text filter column is sized to.
// Layout writes only attributes, so it doesn't retrigger this.
const TABLE_CONTENT_MUTATIONS = { childList: true, subtree: true, characterData: true }

/**
 * Keeps the table's columns fitted to the wrapper: percentage/equal shares while there is room,
 * titles wrapping before the table scrolls sideways (see columnWidths.js).
 */
const useColumnLayout = (containerRef, configuredColumns, loading) => {
  useEffect(() => {
    const wrapper = containerRef.current?.querySelector('.Uic-Table-wrapper')
    if (!wrapper) {
      return undefined
    }

    const applyLayout = () => layoutTableToAvailableWidth(wrapper)
    const handleBeforePrint = () => releaseForPrint(wrapper)
    const table = wrapper.querySelector('table')
    layoutTableToAvailableWidth(wrapper, { resetSpecs: true })

    // A page of rows arrives as many mutations; lay out once per frame.
    let frame = null
    const scheduleLayout = () => {
      if (frame === null) {
        frame = requestAnimationFrame(() => {
          frame = null
          applyLayout()
        })
      }
    }

    window.addEventListener('resize', applyLayout)
    window.addEventListener('beforeprint', handleBeforePrint)
    window.addEventListener('afterprint', applyLayout)
    // jsdom has neither observer; the window `resize` listener covers it there.
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(applyLayout)
    resizeObserver?.observe(wrapper)
    resizeObserver?.observe(document.documentElement)
    const contentObserver =
      table && typeof MutationObserver !== 'undefined' ? new MutationObserver(scheduleLayout) : null
    contentObserver?.observe(table, TABLE_CONTENT_MUTATIONS)

    return () => {
      resizeObserver?.disconnect()
      contentObserver?.disconnect()
      if (frame !== null) {
        cancelAnimationFrame(frame)
      }
      window.removeEventListener('resize', applyLayout)
      window.removeEventListener('beforeprint', handleBeforePrint)
      window.removeEventListener('afterprint', applyLayout)
    }
  }, [containerRef, configuredColumns, loading])
}

export default useColumnLayout
